import {
    context,
    doResize,
    initGL,
    initTexture,
    setState,
} from './util.js';

// Resize utility functions
function onResize() {
    const {width} = context;

    const dpr = window.devicePixelRatio;
    const newWidth = window.innerWidth > 600 ? (500 * dpr) : (300 * dpr);

    if (newWidth !== width) {
        doResize(newWidth, newWidth);
        reset();
    }
}

// Texture initialization
function encode(value) {
    // Encodes float value (between 0 and 1) to RGBA pixel
    value = Math.max(Math.min(value, 1), 0);
    const output = [];
    while (output.length < 4) {
        value *= 256.0;
        let pixel = Math.min(Math.floor(value), 255);
        value -= pixel;
        output.push(pixel);
    }
    return output;
}

function decode(pixel) {
    // Decodes RGBA pixel to float between 0 and 1
    let value = 0;
    for (let i = 3; i >= 0; i--) {
        value += pixel[i];
        value /= 256;
    }
    return value;
}

function getInitialTexture() {
    const {width, height} = context;

    // Initialize Leibniz terms
    const contents = [];
    for (let i = 0; i < width * height; i++) {
        let y = Math.floor(i / width);
        let x = i - (y * width);

        x += 0.5;
        y += 0.5;

        const isInCircle = (u, v) => {
            const r = Math.hypot(u/width - 0.5, v/height - 0.5);
            return (r < 0.5) ? 1 : 0;
        }

        const value = 0.25 * (
            isInCircle(x - 0.25, y - 0.25)
            + isInCircle(x - 0.25, y + 0.25)
            + isInCircle(x + 0.25, y - 0.25)
            + isInCircle(x + 0.25, y + 0.25)
        );

        contents.push(...encode(value));
    }

    return contents;
}



// Main code
function initialize(canvas) {
    context.state = {
        pass: null,
        N: null,
        random_seed: null,
    };

    initGL(canvas);

    window.addEventListener('resize', onResize);
    onResize();

    setState('N', 50);
}

function cleanUp() {
    const {gl} = context;

    window.removeEventListener('resize', onResize);

    context.canvas = null;
    context.gl = null;
    context.program = null;

    context.width = 0;
    context.height = 0;

    gl.deleteTexture(context.current);
    gl.deleteTexture(context.next);
    gl.deleteFramebuffer(context.framebuffer);

    context.current = null;
    context.next = null;
    context.framebuffer = null;
}

function step() {
    const {gl, framebuffer, current} = context;

    // Render to texture
    setState('pass', 0);
    gl.bindTexture(gl.TEXTURE_2D, current);
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.framebufferTexture2D(
        gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0,
        gl.TEXTURE_2D, context.next, 0
    );
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    // Swap textures
    context.current = context.next;
    context.next = current;

    render();
}


function reset() {
    const contents = getInitialTexture();

    context.current = initTexture(contents);
    context.next = initTexture(contents);

    setState('random_seed', Math.random());

    render();
}

function render() {
    const {gl, current} = context;

    setState('pass', 1);
    gl.bindTexture(gl.TEXTURE_2D, current);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
}


function getValue() {
    const {gl, framebuffer} = context;

    const pixel = new Uint8Array(4);
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.readPixels(0, 0, 1, 1, gl.RGBA, gl.UNSIGNED_BYTE, pixel);

    return 4 * decode(pixel);
}

function getMetadata() {
    const {width, height, state: {N}} = context;
    const quality = Math.sqrt(N / Math.log(width * height));
    return [width, height, N, Math.max(quality, 1)];
}

function setKernelSize(n) {setState('N', n);}

export {
    initialize, step, reset,
    cleanUp,
    getValue, getMetadata,
    setKernelSize
};
